import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Checkbox } from "./ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLAN_TYPES = [
  "Commercial — fully insured",
  "Commercial — self-funded (ERISA)",
  "Medicare Advantage",
  "Medicaid MCO",
  "Traditional Medicare",
  "TRICARE",
];

const REASON_CODES = [
  "No out-of-network benefits",
  "HMO",
  "Federal program",
  "Benefits exhausted",
  "Coverage terminated",
  "Other",
];

const GATES = [
  { key: "g1", label: "Gate 1 · Plan type & benefits", hint: "Patient holds out-of-network benefits; plan category verified by card review / scripted carrier call (reference number recorded). Beware the MA trap — an MA card looks commercial." },
  { key: "g2", label: "Gate 2 · Facility", hint: "The facility is in-network with the patient's plan." },
  { key: "g3", label: "Gate 3 · Pathway", hint: "Apply the state matrix row (A-5). Funding status is not determined in non-bifurcated states (UT/AZ); bifurcated states require it — e.g. CA: OON benefits + (self-funded OR out-of-state origination)." },
  { key: "g4", label: "Gate 4 · Service", hint: "Assistant-at-surgery is billable and defensible — documentation will support medical necessity of the assist." },
  { key: "g5", label: "Gate 5 · State law", hint: "No state-specific bar or unmet requirement per the matrix." },
];

const emptyGates = () => Object.fromEntries(GATES.map((g) => [g.key, { result: "", evidence: "" }]));

export default function PreclearForm({ active }) {
  const [caseId, setCaseId] = useState("");
  const [facility, setFacility] = useState("");
  const [procedure, setProcedure] = useState("");
  const [state, setState] = useState("");
  const [dos, setDos] = useState("");
  const [idsVerified, setIdsVerified] = useState(false);
  const [planType, setPlanType] = useState("");
  const [gates, setGates] = useState(emptyGates());
  const [reason, setReason] = useState("");
  const [reasonCode, setReasonCode] = useState("");
  const [decidedBy, setDecidedBy] = useState("");
  const [signature, setSignature] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [log, setLog] = useState(null);

  const fetchLog = useCallback(() => {
    axios.get(`${API}/preclear`).then((r) => setLog(r.data)).catch(() => setLog([]));
  }, []);
  useEffect(() => { if (active === "a4") fetchLog(); }, [active, fetchLog]);

  const commercial = planType.startsWith("Commercial");
  const allAnswered = GATES.every((g) => gates[g.key].result);
  const allPass = GATES.every((g) => gates[g.key].result === "PASS");
  const decision = allAnswered && planType ? (commercial && allPass ? "ACCEPT" : "DECLINE") : "";

  const canSubmit = caseId.trim() && facility.trim() && procedure.trim() && state.trim() && idsVerified &&
    planType && allAnswered && decidedBy.trim() && signature.trim() &&
    (decision !== "DECLINE" || (reasonCode && (reasonCode !== "Other" || reason.trim()))) && decision && !submitting;

  const setGate = (key, field, value) => setGates((p) => ({ ...p, [key]: { ...p[key], [field]: value } }));

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/preclear`, {
        case_id: caseId, facility, procedure, state, date_of_service: dos,
        identifiers_verified: idsVerified, plan_type: planType, gates,
        decision, decline_reason: reason, decline_reason_code: decision === "DECLINE" ? reasonCode : "", decided_by: decidedBy, signature,
      });
      toast.success(`Intake determination ${decision === "ACCEPT" ? "ACCEPTED" : "INELIGIBLE"} recorded for ${caseId}`);
      setCaseId(""); setFacility(""); setProcedure(""); setState(""); setDos("");
      setIdsVerified(false); setPlanType(""); setGates(emptyGates()); setReason(""); setReasonCode("");
      setDecidedBy(""); setSignature("");
      fetchLog();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not record checklist");
    }
    setSubmitting(false);
  };

  return (
    <section className={`chapter utility${active === "a4" ? " active" : ""}`} id="a4">
      <div className="eyebrow">Working record · Appendix A-4 · executes ECH-SOP-11.1/11.2 at claim intake</div>
      <h1>Claim-Intake Eligibility Determination</h1>
      <p className="lede">Recorded at <b>claim intake — after the case is performed and turned in</b>, on the monthly cycle the provider-payment decision needs. Five checks, answered with evidence; the decision is computed, not chosen: ACCEPTED requires all five PASS on a commercial plan — any fail, or any federal-program plan type, is INELIGIBLE with the reason noted (sorted to the provider's ShareFile folder; the loss is absorbed by the independent). Marginal calls escalate to the Ops member, never resolved by optimism.</p>

      <div className="formcard" data-testid="preclear-form">
        <div className="formcard-title">Case &amp; insurance capture (SOP 11.1)</div>
        <div className="fld-grid three">
          <div className="fld"><label>ECH case ID</label><input className="inp" value={caseId} onChange={(e) => setCaseId(e.target.value)} placeholder="ECH-2026-0142" data-testid="pc-case-id" /></div>
          <div className="fld"><label>Facility</label><input className="inp" value={facility} onChange={(e) => setFacility(e.target.value)} placeholder="St. Mark's Surgical Center" data-testid="pc-facility" /></div>
          <div className="fld"><label>Procedure</label><input className="inp" value={procedure} onChange={(e) => setProcedure(e.target.value)} placeholder="Lumbar fusion, assist-at-surgery" data-testid="pc-procedure" /></div>
          <div className="fld"><label>State</label><input className="inp" value={state} onChange={(e) => setState(e.target.value)} placeholder="UT" data-testid="pc-state" /></div>
          <div className="fld"><label>Date of service</label><input className="inp" type="date" value={dos} onChange={(e) => setDos(e.target.value)} data-testid="pc-dos" /></div>
          <div className="fld"><label>Plan type (the first gate)</label>
            <Select value={planType} onValueChange={setPlanType}>
              <SelectTrigger className="inp sel" data-testid="pc-plan-type"><SelectValue placeholder="Determine plan category…" /></SelectTrigger>
              <SelectContent>{PLAN_TYPES.map((p) => (<SelectItem key={p} value={p}>{p}</SelectItem>))}</SelectContent>
            </Select>
          </div>
        </div>
        {planType && !commercial && (
          <div className="pc-federal-warn" data-testid="pc-federal-warn">Federal-program plan type — no NSA/IDR collection path. This case must be DECLINED and escalated to the Compliance Officer (Ch. 3).</div>
        )}
        <label className="agree-row">
          <Checkbox checked={idsVerified} onCheckedChange={(v) => setIdsVerified(!!v)} data-testid="pc-ids-verified" />
          <span>Patient identity verified on <b>two identifiers</b> (name + DOB or member ID) and insurance card / plan details captured to the case record.</span>
        </label>

        <div className="formcard-title" style={{ marginTop: 8 }}>The five checks (SOP 11.2)</div>
        {GATES.map((g) => (
          <div className="pc-gate" key={g.key} data-testid={`pc-gate-${g.key}`}>
            <div className="pc-gate-head">
              <span className="pc-gate-label">{g.label}</span>
              <div className="pc-passfail">
                {["PASS", "FAIL"].map((r) => (
                  <button key={r} type="button"
                    className={`pf-btn ${r.toLowerCase()}${gates[g.key].result === r ? " on" : ""}`}
                    onClick={() => setGate(g.key, "result", r)}
                    data-testid={`pc-${g.key}-${r.toLowerCase()}`}>{r}</button>
                ))}
              </div>
            </div>
            <div className="pc-gate-hint">{g.hint}</div>
            <input className="inp" placeholder="Evidence — what was checked, reference numbers…" value={gates[g.key].evidence}
              onChange={(e) => setGate(g.key, "evidence", e.target.value)} data-testid={`pc-${g.key}-evidence`} />
          </div>
        ))}

        {decision && (
          <div className={`pc-decision ${decision.toLowerCase()}`} data-testid="pc-decision">
            Computed decision: <b>{decision === "ACCEPT" ? "ACCEPTED" : "INELIGIBLE"}</b>{decision === "ACCEPT" ? " — all five checks clear on a commercial plan. The claim proceeds to billing handoff." : " — a check failed or the plan is federal-program. Sorted to the provider's ShareFile folder with the reason noted; not billed, absorbed by the independent."}
          </div>
        )}
        {decision === "DECLINE" && (
          <>
            <div className="fld" style={{ marginBottom: 12, maxWidth: 420 }}>
              <label>Ineligibility reason code (controlled list — reports by cause)</label>
              <Select value={reasonCode} onValueChange={setReasonCode}>
                <SelectTrigger className="inp sel" data-testid="pc-reason-code"><SelectValue placeholder="Select the cause…" /></SelectTrigger>
                <SelectContent>{REASON_CODES.map((r) => (<SelectItem key={r} value={r}>{r}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div className="fld" style={{ marginBottom: 12 }}>
              <label>Detail{reasonCode === "Other" ? " (required for Other)" : " (optional)"} — noted in the ShareFile sort</label>
              <textarea className="inp ta" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Which check failed and why…" data-testid="pc-decline-reason" />
            </div>
          </>
        )}
        <div className="fld-grid">
          <div className="fld"><label>Decided by</label><input className="inp" value={decidedBy} onChange={(e) => setDecidedBy(e.target.value)} placeholder="Name — Ops lane" data-testid="pc-decided-by" /></div>
          <div className="fld"><label>Typed signature</label><input className="inp sig" value={signature} onChange={(e) => setSignature(e.target.value)} placeholder="Type your full name as signature" data-testid="pc-signature" /></div>
        </div>
        <button className="btn" onClick={submit} disabled={!canSubmit} data-testid="pc-submit">Record intake determination</button>
      </div>

      <h2>Determination log</h2>
      <table data-testid="preclear-log">
        <tbody>
          <tr><th>Case</th><th>DOS</th><th>Facility</th><th>Procedure</th><th>Plan type</th><th>Decision</th><th>Decided by</th><th>Recorded</th></tr>
          {log === null ? (
            <tr><td colSpan={8}>Loading…</td></tr>
          ) : log.length === 0 ? (
            <tr><td colSpan={8}>No checklists recorded yet.</td></tr>
          ) : (
            log.map((r) => (
              <tr key={r.id}>
                <td className="q">{r.case_id}</td>
                <td>{r.date_of_service || "—"}</td>
                <td>{r.facility}</td>
                <td>{r.procedure}</td>
                <td>{r.plan_type}</td>
                <td><span className={`vr-pill ${r.decision === "ACCEPT" ? "cleared" : "declined"}`}>{r.decision === "ACCEPT" ? "ACCEPTED" : "INELIGIBLE"}</span>{r.decline_reason_code ? <div className="cap-sub">{r.decline_reason_code}</div> : null}</td>
                <td>{r.decided_by}</td>
                <td>{new Date(r.submitted_at).toLocaleDateString("en-US")}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      <p style={{ color: "var(--mute)", fontSize: "13px" }}>Completeness of determination (100% of turned-in cases sorted) is the MOCR A-2 compliance standard; the ineligibility rate (~30% typical) reports separately as an operating metric. Completed determinations retain 10 years with the case record.</p>
    </section>
  );
}
